import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import TournamentBracket from './TournamentBracket';
import TournamentBracketTree from './TournamentBracketTree';
import Toast from '../../Shared/Toast';
import Modal from '../../Shared/Modal';
import styles from './TournamentDetail.module.css';
import { formatDateTime } from '../../../utils/dateFormat';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';

interface Mercenary {
  id: number;
  name: string;
}

interface Participant {
  id: number;
  mercenary?: Mercenary;
  beast?: { id: number; name: string };
  botMercenary?: { id: number; name: string };
  ranking?: number;
}

interface Match {
  id: number;
  team1Participants: Mercenary[];
  team2Participants: Mercenary[];
  winner_team?: number;
  round_number: number;
  battle_report?: number;
  match_type?: 'normal' | 'final' | 'third_place' | 'bye';
}

interface Tournament {
  id: number;
  name: string;
  description?: string;
  type: string;
  status: string;
  num_teams: number;
  team_size: number;
  start_time: string;
  silver_reward: number;
  top_3_rewards: string | Record<string, number>;
  item_rewards: string | Record<string, string>;
  race_requirement: string;
  min_level: number;
  max_level: number;
  legend: boolean;
  fill_with_beasts?: boolean;
  isSeasonEvent?: boolean;
  TournamentParticipants: Participant[];
  TournamentMatches: Match[];
}

const STRATEGIES = [
  'Normal', 'Normal: Light Attacks', 'Normal: Heavy Attacks',
  'Offensive', 'Offensive: Light Attacks', 'Offensive: Heavy Attacks',
  'Defensive', 'Defensive: Light Attacks', 'Defensive: Heavy Attacks',
  'Berserk', 'Berserk: Light Attacks', 'Berserk: Heavy Attacks',
];

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  locked: 'Locked',
  ongoing: 'Ongoing',
  completed: 'Completed',
};

const TournamentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [notFound, setNotFound] = useState(false);
  const { mercenary } = useMercenary();
  const { socket } = useSocket();
  const [isParticipant, setIsParticipant] = useState(false);
  const [surrenderAt, setSurrenderAt] = useState<number>(50);
  const [strategy, setStrategy] = useState<string>('Normal');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [bracketView, setBracketView] = useState<'rounds' | 'tree'>('rounds');
  const [bettingStats, setBettingStats] = useState<any>(null);
  const [betAmount, setBetAmount] = useState<string>('100');
  const [selectedParticipant, setSelectedParticipant] = useState<number | null>(null);
  const [bettingClosed, setBettingClosed] = useState<boolean>(false);
  const [showJoinConfirm, setShowJoinConfirm] = useState(false);

  useEffect(() => {
    if (!mercenary?.id) return;
    const prefs = loadBattlePrefs(mercenary.id, 'tournament', {
      strategy: 'Normal',
      surrenderHP: 50,
    });
    setStrategy((prefs.strategy as string) || 'Normal');
    setSurrenderAt((prefs.surrenderHP as number) ?? 50);
  }, [mercenary?.id]);

  useEffect(() => {
    if (!mercenary?.id) return;
    saveBattlePrefs(mercenary.id, 'tournament', {
      strategy,
      surrenderHP: surrenderAt,
    });
  }, [mercenary?.id, strategy, surrenderAt]);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const fetchTournament = async () => {
    try {
      const response = await axios.get(`/api/tournament/${id}`, { withCredentials: true });
      setTournament(response.data);
    } catch (error: any) {
      logError(error, 'TournamentDetail.fetchTournament');
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        displayToast(getErrorMessage(error, 'Failed to load tournament'), 'error');
      }
    }
  };

  const fetchBettingStats = async () => {
    try {
      const response = await axios.get(`/api/tournament/${id}/bets`, { withCredentials: true });
      setBettingStats(response.data);
    } catch (error) {
      console.error('Error fetching betting stats:', error);
    }
  };

  // Fetch tournament data independently of mercenary context
  useEffect(() => { fetchTournament(); }, [id]);

  // Update participant status whenever tournament data or mercenary changes
  useEffect(() => {
    if (!tournament) return;
    const found = tournament.TournamentParticipants.some(
      (p: Participant) => p.mercenary && p.mercenary.id === mercenary?.id
    );
    setIsParticipant(!!mercenary?.id && found);
  }, [tournament, mercenary?.id]);

  useEffect(() => {
    if (tournament) {
      const startTime = new Date(tournament.start_time);
      const minutesUntilStart = (startTime.getTime() - Date.now()) / 60000;
      
      // Betting is only allowed during 'locked' status and before tournament starts
      const isBettingOpen = tournament.status === 'locked' && minutesUntilStart > 0;
      setBettingClosed(!isBettingOpen);
      
      fetchBettingStats();
    }
  }, [tournament]);

  // Listen for tournament roster updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    const handleRosterUpdate = (data: any) => {
      // Only update if it's for the current tournament
      if (data.tournamentId === parseInt(id)) {
        console.log('Tournament roster update:', data);
        
        // Refresh tournament data to get updated participant list
        fetchTournament();
        
        // Show notification based on action
        if (data.action === 'join') {
          displayToast(`${data.mercenaryName} joined the tournament (${data.participantCount}/${data.maxParticipants})`, 'success');
        } else if (data.action === 'leave') {
          displayToast(`${data.mercenaryName} left the tournament (${data.participantCount}/${data.maxParticipants})`, 'error');
        } else if (data.action === 'death') {
          displayToast(`${data.mercenaryName} has died and been removed from the tournament`, 'error');
        }
      }
    };

    socket.on('tournament_roster_update', handleRosterUpdate);

    return () => {
      socket.off('tournament_roster_update', handleRosterUpdate);
    };
  }, [socket, id]);

  const performJoin = async () => {
    if (!tournament) return;
    setShowJoinConfirm(false);
    try {
      await axios.post(
        `/api/tournament/${id}/join`,
        { mercenaryId: mercenary?.id, surrenderHP: surrenderAt / 100, strategy },
        { withCredentials: true }
      );
      await fetchTournament();
      displayToast('Successfully joined the tournament!', 'success');
    } catch (error: any) {
      if (!error?.response || error.response.status >= 500) {
        logError(error, 'TournamentDetail.handleJoin');
      }
      displayToast(getErrorMessage(error, 'Failed to join the tournament'), 'error');
    }
  };

  const handleJoin = () => {
    if (!tournament) return;
    if (requiresJoinConfirm) {
      setShowJoinConfirm(true);
      return;
    }
    performJoin();
  };

  const handleLeave = async () => {
    try {
      await axios.post(`/api/tournament/${id}/leave`, { mercenaryId: mercenary?.id }, { withCredentials: true });
      await fetchTournament();
      displayToast('Successfully left the tournament.', 'success');
    } catch (error) {
      displayToast('Failed to leave the tournament.', 'error');
    }
  };

  const handlePlaceBet = async () => {
    if (!selectedParticipant || !betAmount || parseInt(betAmount) < 1) {
      displayToast('Please select a participant and enter a valid bet amount', 'error');
      return;
    }
    try {
      await axios.post(
        `/api/tournament/${id}/bet`,
        { participantId: selectedParticipant, betAmount: parseInt(betAmount) },
        { withCredentials: true }
      );
      await fetchBettingStats();
      await fetchTournament();
      displayToast('Bet placed successfully!', 'success');
      setSelectedParticipant(null);
      setBetAmount('100');
    } catch (error: any) {
      displayToast(error.response?.data?.error || 'Failed to place bet', 'error');
    }
  };

  if (notFound) {
    return (
      <div className={styles.loading}>
        <p style={{ color: 'var(--color-text-secondary, #aaa)', fontSize: '1.1rem' }}>Tournament not found.</p>
        <p style={{ color: 'var(--color-text-secondary, #888)', fontSize: '0.9rem', marginTop: 8 }}>It may have been cancelled or the link is outdated.</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        Loading tournament...
      </div>
    );
  }

  const parseRewards = () => {
    try {
      const top3Rewards = typeof tournament.top_3_rewards === 'string'
        ? JSON.parse(tournament.top_3_rewards) : (tournament.top_3_rewards ?? {});
      const itemRewards = typeof tournament.item_rewards === 'string'
        ? JSON.parse(tournament.item_rewards) : (tournament.item_rewards ?? {});
      return { top3Rewards: top3Rewards ?? {}, itemRewards: itemRewards ?? {} };
    } catch {
      return { top3Rewards: {}, itemRewards: {} };
    }
  };

  const { top3Rewards, itemRewards } = parseRewards();
  const isCompleted = tournament.status === 'completed';
  const isBracketVisible = ['locked', 'ongoing', 'completed'].includes(tournament.status);
  const isDeath = tournament.type === 'death';
  const requiresJoinConfirm = isDeath || tournament.legend;

  const GUILD_TOKEN_RACE_TYPES = ['Elf', 'Dwarf', 'Human', 'Goblin', 'Orc', 'Troll', 'Undead', 'Red Blood', 'Black Blood'];
  const isGuildTokenTournament = !!tournament.isSeasonEvent && GUILD_TOKEN_RACE_TYPES.includes(tournament.race_requirement);

  const getWinnerByRank = (rank: number): { name: string; id: number | null; type: string } | null => {
    const p = tournament.TournamentParticipants.find((p: any) => p.ranking === rank);
    if (!p) return null;
    if (p.mercenary) return { name: p.mercenary.name, id: p.mercenary.id, type: 'mercenary' };
    if (p.botMercenary) return { name: p.botMercenary.name, id: p.botMercenary.id, type: 'bot' };
    if (p.beast) return { name: p.beast.name, id: p.beast.id, type: 'beast' };
    return null;
  };

  const totalSlots = tournament.num_teams * tournament.team_size;
  const filled = tournament.TournamentParticipants.length;
  const fillPct = totalSlots > 0 ? Math.round((filled / totalSlots) * 100) : 0;

  const isAlreadyLegend = tournament.legend && (
    (mercenary as any)?.legend === true ||
    (mercenary as any)?.title === 'Legend' ||
    ((mercenary as any)?.legendSeasonIds?.length > 0)
  );

  const PODIUM = ['1st', '2nd', '3rd'] as const;
  const PODIUM_ICONS = ['♔', '♕', '♖'];

  return (
    <div className={styles.container}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}

      <Modal
        show={showJoinConfirm}
        onClose={() => setShowJoinConfirm(false)}
        onConfirm={performJoin}
        hideCloseButton
        title={tournament.legend ? 'Join Legend Tournament?' : 'Join Death Tournament?'}
        message={(
          <div>
            <p>
              Battles are fought to the death and you cannot withdraw after joining this tournament.
            </p>
            <p>
              <strong>Risk:</strong> Your mercenary will die permanently if defeated.
            </p>
            <p>Do you still want to enlist?</p>
          </div>
        )}
      />

      {/* ── Banner ── */}
      <div className={`${styles.banner} ${isDeath ? styles.bannerDeath : ''} ${tournament.legend ? styles.bannerLegend : ''}`}>
        <div className={styles.bannerBadges}>
          {tournament.legend && <span className={styles.legendBadge}>&#9812; LEGEND</span>}
          <span className={`${styles.typeBadge} ${isDeath ? styles.deathBadge : styles.normalBadge}`}>
            {isDeath ? 'DEATH MATCH' : 'NORMAL BATTLE'}
          </span>
          <span className={`${styles.fillBadge} ${tournament.fill_with_beasts ? styles.fillOn : styles.fillOff}`}>
            {tournament.fill_with_beasts ? '✦ PvE Fill' : '✕ Players Only'}
          </span>
          <span className={`${styles.statusBadge} ${styles[`status_${tournament.status}`]}`}>
            {STATUS_LABELS[tournament.status] || tournament.status}
          </span>
        </div>

        <h2 className={styles.tournamentName}>{tournament.name}</h2>

        {tournament.description && (
          <p className={styles.description}>{tournament.description}</p>
        )}

        <div className={styles.bannerMeta}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Level Range</span>
            <span className={styles.metaValue}>{tournament.min_level} – {tournament.max_level}</span>
          </div>
          {tournament.team_size > 1 && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Team Size</span>
              <span className={styles.metaValue}>{tournament.team_size}v{tournament.team_size}</span>
            </div>
          )}
          {tournament.race_requirement && tournament.race_requirement !== 'All' && (
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Race</span>
              <span className={styles.metaValue}>{tournament.race_requirement}</span>
            </div>
          )}
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Starts</span>
            <span className={styles.metaValue}>
              {formatDateTime(new Date(tournament.start_time))}
            </span>
          </div>

          {tournament.status === 'scheduled' && (() => {
            const closeTime = new Date(new Date(tournament.start_time).getTime() - 60 * 60 * 1000);
            const isPast = closeTime <= new Date();
            return (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Signup closes</span>
                <span className={styles.metaValue} style={{ color: isPast ? 'var(--color-status-critical, #e07070)' : 'inherit' }}>
                  {isPast ? 'Closed' : formatDateTime(closeTime)}
                </span>
              </div>
            );
          })()}

          {/* Participant progress */}
          <div className={styles.metaSlotBlock}>
            <div className={styles.metaSlotLabel}>
              <span>Participants</span>
              <span>{filled} / {totalSlots}</span>
            </div>
            <div className={styles.metaSlotTrack}>
              <div
                className={`${styles.metaSlotFill} ${fillPct >= 100 ? styles.metaSlotFull : ''}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.body}>

        {/* ── Rewards Panel ── */}
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Rewards</h3>

          {/* Podium */}
          <div className={styles.podium}>
            {PODIUM.map((placement, idx) => {
              const rank = idx + 1;
              const silverReward = top3Rewards[placement] || top3Rewards[`${rank}`] || 0;
              const itemReward = itemRewards[placement] || itemRewards[`${rank}`] || '';
              const showGuildToken = isGuildTokenTournament && placement === '1st';
              const winner = isCompleted ? getWinnerByRank(rank) : null;
              if (!silverReward && !itemReward && !showGuildToken) return null;
              return (
                <div key={placement} className={`${styles.podiumCard} ${styles[`podium_${rank}`]}`}>
                  <div className={styles.podiumCardTop}>
                    <span className={styles.podiumIcon}>{PODIUM_ICONS[idx]}</span>
                    <span className={styles.podiumPlace}>{placement}</span>
                    {silverReward > 0 && (
                      <span className={styles.podiumSilver}>{silverReward.toLocaleString()} silver</span>
                    )}
                    {itemReward && <span className={styles.podiumItem}>{itemReward}</span>}
                    {showGuildToken && <span className={styles.podiumItem}>Guild Token</span>}
                    {winner && (
                      <span className={styles.podiumWinnerInline}>
                        {winner.type === 'mercenary' && winner.id ? (
                          <Link to={`/mercenary/${winner.id}`} className={styles.podiumWinnerLink}>
                            {winner.name}
                          </Link>
                        ) : (
                          <span className={styles.podiumWinner}>{winner.name}</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {tournament.silver_reward > 0 && (
            <div className={styles.participationReward}>
              <span className={styles.partLabel}>Participation:</span>
              <span className={styles.partValue}>{tournament.silver_reward.toLocaleString()} silver</span>
            </div>
          )}
        </div>

        {/* ── Betting Panel ── */}
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Betting Pool</h3>
          <div className={styles.betStats}>
            {[
              ['Total Silver Bet', (bettingStats?.totalBetAmount ?? 0).toLocaleString()],
              ['Total Bettors', bettingStats?.totalBettors ?? 0],
              ['Winners', bettingStats?.winners ?? 0],
              ['Total Payouts', `${(bettingStats?.totalPayouts ?? 0).toLocaleString()} silver`],
            ].map(([label, value]) => (
              <div key={String(label)} className={styles.betStat}>
                <span className={styles.betStatLabel}>{label}</span>
                <span className={styles.betStatValue}>{value}</span>
              </div>
            ))}
          </div>

          {tournament.status === 'locked' && !bettingClosed && (
            <div className={styles.betForm}>
              <p className={styles.betInstruction}>Place a bet on the winner:</p>
              <div className={styles.betParticipants}>
                {tournament.TournamentParticipants.map((p: Participant) => {
                  const name = p.mercenary?.name || p.botMercenary?.name || p.beast?.name || 'Unknown';
                  return (
                    <div
                      key={p.id}
                      className={`${styles.betParticipant} ${selectedParticipant === p.id ? styles.betSelected : ''}`}
                      onClick={() => setSelectedParticipant(p.id)}
                    >
                      {name}
                    </div>
                  );
                })}
              </div>
              {selectedParticipant && (
                <div className={styles.betInputRow}>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    placeholder="Amount"
                    min="1"
                    className={styles.betInput}
                  />
                  <button onClick={handlePlaceBet} className={styles.betButton}>Place Bet</button>
                </div>
              )}
            </div>
          )}

          {(tournament.status === 'scheduled' || bettingClosed) && tournament.status !== 'completed' && (
            <div className={styles.betClosed}>
              {tournament.status === 'scheduled' 
                ? 'Betting opens when the tournament locks (1 hour before start)' 
                : 'Betting is closed'}
            </div>
          )}
        </div>

        {/* ── Join / Leave Panel ── */}
        {tournament.status === 'scheduled' && (
          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>{isParticipant ? 'Registration' : 'Join Tournament'}</h3>

            {isParticipant ? (
              <div>
                <div className={styles.enrolled}>You are enrolled in this tournament.</div>
                {!isDeath && (
                  <button onClick={handleLeave} className={styles.leaveBtn}>Leave Tournament</button>
                )}
                {isDeath && (
                  <div className={styles.deathLock}>
                    Death match - withdrawal is not permitted.
                  </div>
                )}
              </div>
            ) : isAlreadyLegend ? (
              <div className={styles.deathLock}>
                {(mercenary as any)?.name ?? 'Your mercenary'} has already earned the Legend title and cannot enter another Legend Tournament.
              </div>
            ) : (
              <div className={styles.joinForm}>
                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>Surrender at</label>
                  <select
                    className={styles.fieldSelect}
                    value={surrenderAt}
                    onChange={(e) => setSurrenderAt(parseInt(e.target.value))}
                  >
                    {[90,80,70,60,50,40,30,20,10,0].map(v => (
                      <option key={v} value={v}>{v}% HP</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formField}>
                  <label className={styles.fieldLabel}>Strategy</label>
                  <select
                    className={styles.fieldSelect}
                    value={strategy}
                    onChange={(e) => setStrategy(e.target.value)}
                  >
                    {STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <button onClick={handleJoin} className={styles.joinBtn}>Join Tournament</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Bracket Section ── */}
      {isBracketVisible ? (
        <div className={styles.bracketSection}>
          <div className={styles.bracketHeader}>
            <h3 className={styles.bracketTitle}>Tournament Bracket</h3>
            <div className={styles.viewToggleGroup}>
              <button
                className={`${styles.viewToggle} ${bracketView === 'rounds' ? styles.toggleActive : ''}`}
                onClick={() => setBracketView('rounds')}
              >
                Round View
              </button>
              <button
                className={`${styles.viewToggle} ${bracketView === 'tree' ? styles.toggleActive : ''}`}
                onClick={() => setBracketView('tree')}
              >
                Tree View
              </button>
            </div>
          </div>

          {bracketView === 'rounds' ? (
            <TournamentBracket matches={tournament.TournamentMatches} activeMercenaryId={mercenary?.id} />
          ) : (
            <TournamentBracketTree
              matches={tournament.TournamentMatches}
              numTeams={tournament.num_teams}
            />
          )}
        </div>
      ) : (
        <div className={styles.bracketPending}>
          Bracket will be revealed once the tournament begins.
        </div>
      )}
    </div>
  );
};

export default TournamentDetail;
